import { useEffect, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { ClockCounterClockwise, Plus, PencilSimple, Trash, MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";

const ACTION_STYLE = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  delete: "bg-red-50 text-red-700 border-red-200",
};
const ACTION_ICON = { create: Plus, update: PencilSimple, delete: Trash };
const ACTION_LABEL = { create: "Creazione", update: "Modifica", delete: "Eliminazione" };
const ENTITY_LABEL = {
  user: "Utente", product: "Prodotto", warehouse: "Magazzino",
  category: "Categoria", supplier: "Fornitore",
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (entity) params.entity = entity;
      if (action) params.action = action;
      if (q) params.user_email = q;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/audit", { params });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [entity, action, q, dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const t = localStorage.getItem("token");
      const qs = new URLSearchParams();
      if (entity) qs.set("entity", entity);
      if (action) qs.set("action", action);
      if (q) qs.set("user_email", q);
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      const res = await fetch(`${API_BASE}/audit/export?${qs.toString()}`, {
        credentials: "include",
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = dateFrom || dateTo ? `_${dateFrom || "inizio"}_${dateTo || "oggi"}` : "";
      a.href = url; a.download = `audit-log${suffix}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Audit log esportato");
    } catch (e) { toast.error(formatApiError(e) || "Errore durante l'export"); }
    finally { setExporting(false); }
  };

  const resetFilters = () => {
    setEntity(""); setAction(""); setQ(""); setDateFrom(""); setDateTo("");
  };

  const hasActiveFilters = entity || action || q || dateFrom || dateTo;

  return (
    <div>
      <PageHeader
        title="Audit Log"
        subtitle="Storico completo delle azioni admin su utenti, prodotti, magazzini, categorie e fornitori"
      />

      <div className="p-4 md:p-8 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-white border border-neutral-200 px-3 py-2 flex-1 min-w-[200px] max-w-md">
            <MagnifyingGlass size={16} className="text-neutral-400" />
            <input data-testid="audit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtra per email utente..." className="flex-1 outline-none text-sm bg-transparent" />
          </div>
          <div className="flex border border-neutral-200 bg-white">
            {[["", "Tutte"], ["create", "Creazioni"], ["update", "Modifiche"], ["delete", "Eliminazioni"]].map(([v, l]) => (
              <button key={v} data-testid={`filter-action-${v || "all"}`} onClick={() => setAction(v)}
                      className={`px-3 py-2 text-xs uppercase tracking-wider font-medium transition-colors ${action === v ? "bg-[#002FA7] text-white" : "text-neutral-600 hover:bg-neutral-50"}`}>
                {l}
              </button>
            ))}
          </div>
          <select data-testid="filter-entity" value={entity} onChange={(e) => setEntity(e.target.value)} className="px-3 py-2 border border-neutral-200 text-sm bg-white">
            <option value="">Tutte le entità</option>
            <option value="user">Utenti</option>
            <option value="product">Prodotti</option>
            <option value="warehouse">Magazzini</option>
            <option value="category">Categorie</option>
            <option value="supplier">Fornitori</option>
          </select>
        </div>

        <div className="flex flex-wrap items-end gap-3 bg-white border border-neutral-200 p-3">
          <div className="flex flex-col gap-1">
            <label className="label-eyebrow" htmlFor="audit-date-from">Dal</label>
            <input id="audit-date-from" data-testid="audit-date-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 border border-neutral-200 text-sm bg-white font-mono" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="label-eyebrow" htmlFor="audit-date-to">Al</label>
            <input id="audit-date-to" data-testid="audit-date-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 border border-neutral-200 text-sm bg-white font-mono" />
          </div>
          {hasActiveFilters && (
            <button data-testid="audit-reset-filters" onClick={resetFilters} className="px-3 py-2 border border-neutral-300 text-xs uppercase tracking-wider hover:bg-neutral-50">
              Reset filtri
            </button>
          )}
          <div className="flex-1" />
          <button data-testid="audit-export-xlsx" onClick={exportXlsx} disabled={exporting}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium transition-colors">
            <DownloadSimple size={16} weight="bold" />
            {exporting ? "Export in corso..." : "Esporta XLSX"}
          </button>
        </div>

        <div className="text-xs text-neutral-500" data-testid="audit-row-count">
          {rows.length} {rows.length === 1 ? "record" : "record"} {hasActiveFilters ? "(filtrati)" : ""}
        </div>

        <div className="bg-white border border-neutral-200 overflow-x-auto" data-testid="audit-table">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Quando</th>
                <th className="px-4 py-3 label-eyebrow">Chi</th>
                <th className="px-4 py-3 label-eyebrow">Azione</th>
                <th className="px-4 py-3 label-eyebrow">Entità</th>
                <th className="px-4 py-3 label-eyebrow">Target</th>
                <th className="px-4 py-3 label-eyebrow hidden md:table-cell">Dettagli</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-neutral-500">Nessuna attività registrata con i filtri correnti.</td></tr>
              ) : rows.map((r) => {
                const Icon = ACTION_ICON[r.action] || ClockCounterClockwise;
                return (
                  <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs text-neutral-600">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-mono">{r.user_email}</div>
                      <div className="label-eyebrow">{r.user_role || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs border ${ACTION_STYLE[r.action] || "bg-neutral-50 border-neutral-200"}`}>
                        <Icon size={12} weight="bold" /> {ACTION_LABEL[r.action] || r.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-neutral-700">{ENTITY_LABEL[r.entity] || r.entity}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.entity_name || "—"}</div>
                      <div className="text-[10px] font-mono text-neutral-400">{r.entity_id?.slice(0, 8)}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      {r.changes && Object.keys(r.changes).length > 0 ? (
                        <details>
                          <summary className="cursor-pointer text-xs text-[#002FA7] hover:underline">
                            {Object.keys(r.changes).length} campi
                          </summary>
                          <pre className="text-[10px] font-mono text-neutral-600 mt-1 max-w-md overflow-x-auto whitespace-pre-wrap bg-neutral-50 p-2 border border-neutral-200">
{JSON.stringify(r.changes, null, 2)}
                          </pre>
                        </details>
                      ) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
